import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import config from "../../config";
import AdminLayout from "./AdminLayout";
import {
  categorizeCoursesWithTenseMap,
  categorizeCoursesWithSyntaxMapNavigation,
  categorizeCoursesWithHierarchicalTenseMap,
  TENSE_MAP_CATEGORIES,
  SYNTAX_MAP_NAVIGATION_CATEGORIES,
  HIERARCHICAL_TENSE_MAP_CATEGORIES,
  generateTenseMapIndicator,
} from "./enhancedTenseMapCategories";
import { getStoredRole } from "../../authority";

// Edit Question Form Component
const EditQuestionForm = ({ question, courses, onSave, onCancel }) => {
  const [editData, setEditData] = useState({
    question_id: question.question_id,
    question_title: question.question_title || "",
    answer_title_a: question.answer_title_a || "",
    answer_title_b: question.answer_title_b || "",
    answer_title_c: question.answer_title_c || "",
    answer_title_d: question.answer_title_d || "",
    right_answer: question.right_answer || "a",
    difficulty: question.difficulty || "1",
    course_ids: question.course_ids ? question.course_ids.split(',').map(id => parseInt(id.trim(), 10)) : [],
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(editData);
  };

  const handleCourseToggle = (courseId) => {
    const numericId = parseInt(courseId, 10);
    const updatedCourses = [...editData.course_ids];

    const index = updatedCourses.indexOf(numericId);
    if (index >= 0) {
      updatedCourses.splice(index, 1);
    } else {
      updatedCourses.push(numericId);
    }

    setEditData((prev) => ({
      ...prev,
      course_ids: updatedCourses,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Question
        </label>
        <input
          type="text"
          value={editData.question_title}
          onChange={(e) =>
            setEditData((prev) => ({ ...prev, question_title: e.target.value }))
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Answer A (Correct)
          </label>
          <input
            type="text"
            value={editData.answer_title_a}
            onChange={(e) =>
              setEditData((prev) => ({
                ...prev,
                answer_title_a: e.target.value,
              }))
            }
            className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Answer B
          </label>
          <input
            type="text"
            value={editData.answer_title_b}
            onChange={(e) =>
              setEditData((prev) => ({
                ...prev,
                answer_title_b: e.target.value,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Answer C
          </label>
          <input
            type="text"
            value={editData.answer_title_c}
            onChange={(e) =>
              setEditData((prev) => ({
                ...prev,
                answer_title_c: e.target.value,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Answer D
          </label>
          <input
            type="text"
            value={editData.answer_title_d}
            onChange={(e) =>
              setEditData((prev) => ({
                ...prev,
                answer_title_d: e.target.value,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Difficulty
        </label>
        <div className="flex space-x-4">
          {[
            { value: "1", label: "Easy" },
            { value: "2", label: "Medium" },
            { value: "3", label: "TOEIC" },
          ].map((diff) => (
            <label key={diff.value} className="flex items-center">
              <input
                type="radio"
                name="difficulty"
                value={diff.value}
                checked={editData.difficulty === diff.value}
                onChange={(e) =>
                  setEditData((prev) => ({
                    ...prev,
                    difficulty: e.target.value,
                  }))
                }
                className="mr-2"
              />
              {diff.label}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Courses
        </label>
        <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
          {courses.map((course) => (
            <label key={course.course_id} className="flex items-center mb-1">
              <input
                type="checkbox"
                checked={editData.course_ids.includes(Number(course.course_id))}
                onChange={() => handleCourseToggle(course.course_id)}
                className="mr-2"
              />
              <span className="text-sm">{course.course_item}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Save Changes
        </button>
      </div>
    </form>
  );
};

const FormAddQuestion = ({ fetchQuestions, standalone = false }) => {
  // --- Edit/Delete Modal State ---
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // --- Edit/Delete Handlers ---
  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setShowEditModal(true);
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm("Are you sure you want to delete this question?"))
      return;
    try {
      const response = await fetch(`${config.backendUrl}/quiz/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: localStorage.getItem("jstoken"),
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to delete question (${response.status})`);
      }
      toast.success("Question deleted successfully");
      fetchAllQuestions();
    } catch (error) {
      toast.error("Failed to delete question");
    }
  };

  // Handle question approval
  const handleApproveQuestion = async (question) => {
    try {
      const response = await fetch(
        `${config.backendUrl}/quiz/${question.question_id}/approve`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("jstoken"),
          },
          body: JSON.stringify({
            question_title: question.question_title,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to approve question (${response.status})`);
      }

      toast.success("Question approved successfully!");
      fetchAllQuestions(); // Refresh the questions list
    } catch (error) {
      console.error("Error approving question:", error);
      toast.error("Failed to approve question");
    }
  };

  // Handle question update
  const handleUpdateQuestion = async (updatedQuestion) => {
    try {
      const response = await fetch(
        `${config.backendUrl}/quiz/${updatedQuestion.question_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("jstoken"),
          },
          body: JSON.stringify({
            question_title: updatedQuestion.question_title,
            answer_title_a: updatedQuestion.answer_title_a,
            answer_title_b: updatedQuestion.answer_title_b,
            answer_title_c: updatedQuestion.answer_title_c,
            answer_title_d: updatedQuestion.answer_title_d,
            right_answer: updatedQuestion.right_answer,
            difficulty: updatedQuestion.difficulty,
            course_ids: updatedQuestion.course_ids.join(','),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update question (${response.status})`);
      }

      toast.success("Question updated successfully!");
      setShowEditModal(false);
      setEditingQuestion(null);
      fetchAllQuestions(); // Refresh the questions list
    } catch (error) {
      console.error("Error updating question:", error);
      toast.error("Failed to update question");
    }
  };

  const [formData, setFormData] = useState({
    question: "",
    answers: ["", "", "", ""],
    difficulty: "1",
    course: [],
  });
  const [listCourse, setListCourse] = useState([]);
  const [showModItems, setShowModItems] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // New: State for all questions and loading
  const [questions, setQuestions] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

  // Filter state
  const [selectedCourseFilter, setSelectedCourseFilter] = useState("");
  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Expanded questions state
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  // Check if current user is admin
  const [isAdmin, setIsAdmin] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [questionsPerPage, setQuestionsPerPage] = useState(10);

  // Toggle question expansion
  const toggleQuestionExpansion = (questionId) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  // Pagination functions
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedQuestions(new Set()); // Collapse all when changing pages
  };

  const handleQuestionsPerPageChange = (newPerPage) => {
    setQuestionsPerPage(newPerPage);
    setCurrentPage(1); // Reset to first page
    setExpandedQuestions(new Set());
  };

  // Fetch courses from backend
  const fetchCourses = async () => {
    try {
      const response = await fetch(`${config.backendUrl}/course`, {
        headers: {
          Authorization: localStorage.getItem("jstoken"),
        },
      });
      const data = await response.json();
      setListCourse(data.courses);
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error("Failed to load courses");
    }
  };

  // Fetch all questions from backend
  const fetchAllQuestions = async () => {
    setIsLoadingQuestions(true);
    try {
      const response = await fetch(`${config.backendUrl}/quiz`, {
        headers: {
          Authorization: localStorage.getItem("jstoken"),
        },
      });
      const data = await response.json();
      setQuestions(Array.isArray(data.questions) ? data.questions : []);
    } catch (error) {
      setQuestions([]);
      toast.error("Failed to load questions");
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  useEffect(() => {
    fetchCourses();
    fetchAllQuestions();

    // Check if user is admin
    const userRole = getStoredRole();
    setIsAdmin(userRole === 1);
  }, []);

  const handleQuestionChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      question: e.target.value,
    }));
    setSuccessMessage("");
  };

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...formData.answers];
    newAnswers[index] = value;
    setFormData((prev) => ({
      ...prev,
      answers: newAnswers,
    }));
    setSuccessMessage("");
  };

  const handleDifficultyChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      difficulty: e.target.value,
    }));
    setSuccessMessage("");
  };

  const handleCourseChange = (courseId) => {
    const numericId = parseInt(courseId, 10);
    const updatedCourses = [...formData.course];

    const index = updatedCourses.indexOf(numericId);
    if (index >= 0) {
      updatedCourses.splice(index, 1);
    } else {
      updatedCourses.push(numericId);
    }

    setFormData((prev) => ({
      ...prev,
      course: updatedCourses,
    }));
    setSuccessMessage("");
  };

  const toggleModItems = () => {
    setShowModItems(!showModItems);
  };

  const checkCombinationOrder = (order, id) => {
    let i = 0;
    let tmp = id;

    while (tmp > 0) {
      tmp = tmp - Math.pow(2, Math.floor(Math.log2(tmp)));
      i = i + 1;
    }

    return i === order;
  };

  const isMod = (id) => {
    if (id > 127 && id <= 65536 && !showModItems) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert course array to comma-separated string
      const courseIdsString = formData.course.length > 0 
        ? formData.course.map(id => String(id)).join(',')
        : null;

      // Structure the data according to what the backend expects
      const requestBody = {
        quiz_data: formData.answers
          .map((answer) => answer.trim())
          .filter((answer) => answer),
        question_title: formData.question.trim(),
        course_ids: courseIdsString,
        difficulty: parseInt(formData.difficulty, 10),
        verified: false, // Default new questions to unverified
      };

      console.log("Sending quiz data:", requestBody);

      const response = await fetch(`${config.backendUrl}/quiz`, {
        method: "POST",
        body: JSON.stringify(requestBody),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          Authorization: localStorage.getItem("jstoken"),
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response from server:", errorText);
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      toast.success("Question added successfully!");
      // Refresh questions list after add
      fetchAllQuestions();

      // Reset form
      setFormData({
        question: "",
        answers: ["", "", "", ""],
        difficulty: "1",
        course: [],
      });
      setSuccessMessage(
        `Successfully added question: ${data.msg || "Quiz question created"}`
      );
    } catch (error) {
      console.error("Error adding question:", error);
      toast.error(
        `Failed to add question: ${error.message || "Please try again"}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // State for hierarchical view
  const [useHierarchicalView, setUseHierarchicalView] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set());
  
  // Toggle category expansion
  const toggleCategoryExpansion = (categoryKey) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey);
      // Also collapse all subcategories when collapsing main category
      const newSubExpanded = new Set(expandedSubcategories);
      HIERARCHICAL_TENSE_MAP_CATEGORIES.find(cat => cat.key === categoryKey)?.subcategories?.forEach(sub => {
        newSubExpanded.delete(`${categoryKey}_${sub.key}`);
      });
      setExpandedSubcategories(newSubExpanded);
    } else {
      newExpanded.add(categoryKey);
    }
    setExpandedCategories(newExpanded);
  };

  // Toggle subcategory expansion
  const toggleSubcategoryExpansion = (categoryKey, subcategoryKey) => {
    const key = `${categoryKey}_${subcategoryKey}`;
    const newExpanded = new Set(expandedSubcategories);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedSubcategories(newExpanded);
  };

  // Get categorized courses based on view type
  const getCategorizedData = () => {
    if (useHierarchicalView) {
      return categorizeCoursesWithHierarchicalTenseMap(listCourse);
    } else {
      const { categories: categorizedCourses, positionMap } = categorizeCoursesWithSyntaxMapNavigation(listCourse);
      return { categorizedCourses, positionMap, currentCategories: SYNTAX_MAP_NAVIGATION_CATEGORIES };
    }
  };

  const categorizedData = getCategorizedData();

  // Render with or without AdminLayout based on prop
  const content = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Course Selection Section */}
        <div className="space-y-4">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Select Courses
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">View:</span>
                <button
                  type="button"
                  onClick={() => setUseHierarchicalView(!useHierarchicalView)}
                  className={`px-3 py-1 text-xs rounded-full border transition-all ${
                    useHierarchicalView
                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                      : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {useHierarchicalView ? '🗺️ Tense Map' : '📚 Navigation Paths'}
                </button>
              </div>
            </div>
            <div className="rounded-lg border bg-white shadow-sm p-4 flex flex-col gap-4">
              {useHierarchicalView ? (
                // Hierarchical Tense Map View
                <>
                  {HIERARCHICAL_TENSE_MAP_CATEGORIES.map((category) => (
                    <div key={category.key} className="mb-4">
                      {/* Main Category Header */}
                      <div 
                        className="flex items-center gap-2 mb-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                        onClick={() => toggleCategoryExpansion(category.key)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{category.icon}</span>
                          <div
                            className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                            style={{ backgroundColor: category.color }}
                          ></div>
                          <div className="font-bold text-gray-800 text-lg">
                            {category.label}
                          </div>
                          <span className="text-xs text-gray-500 italic">
                            {category.description}
                          </span>
                        </div>
                        <div className="ml-auto">
                          <svg
                            className={`w-5 h-5 text-gray-400 transition-transform ${
                              expandedCategories.has(category.key) ? 'rotate-90' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>

                      {/* Subcategories */}
                      {expandedCategories.has(category.key) && (
                        <div className="ml-8 space-y-3">
                          {categorizedData.categorizedCourses[category.key]?.subcategories?.map((subcategory) => (
                            <div key={subcategory.key} className="border-l-2 border-gray-200 pl-4">
                              {/* Subcategory Header */}
                              <div 
                                className="flex items-center gap-2 mb-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                                onClick={() => toggleSubcategoryExpansion(category.key, subcategory.key)}
                              >
                                <span className="text-base">{subcategory.icon}</span>
                                <div
                                  className="w-3 h-3 rounded-full border border-white shadow-sm"
                                  style={{ backgroundColor: subcategory.color }}
                                ></div>
                                <div className="font-semibold text-gray-700">
                                  {subcategory.label}
                                </div>
                                <span className="text-xs text-gray-500 italic">
                                  {subcategory.description}
                                </span>
                                <div className="ml-auto">
                                  <svg
                                    className={`w-4 h-4 text-gray-400 transition-transform ${
                                      expandedSubcategories.has(`${category.key}_${subcategory.key}`) ? 'rotate-90' : ''
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </div>

                              {/* Courses under subcategory */}
                              {expandedSubcategories.has(`${category.key}_${subcategory.key}`) && (
                                <div className="ml-6 flex flex-wrap gap-2">
                                  {subcategory.matchedCourses.length === 0 ? (
                                    <span className="text-xs text-gray-400">
                                      No courses found for this subcategory.
                                    </span>
                                  ) : (
                                    subcategory.matchedCourses.map((course) => (
                                      <label
                                        key={course.course_id}
                                        className={`flex items-center gap-1 px-3 py-2 rounded-lg border-2 transition-all duration-150 cursor-pointer select-none
                                          ${
                                            formData.course.includes(Number(course.course_id))
                                              ? `text-white shadow-md border-gray-700`
                                              : `border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50`
                                          }
                                        `}
                                        style={{
                                          backgroundColor: formData.course.includes(
                                            Number(course.course_id)
                                          )
                                            ? subcategory.color
                                            : "white",
                                          borderColor: formData.course.includes(
                                            Number(course.course_id)
                                          )
                                            ? subcategory.color
                                            : undefined,
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          value={course.course_id}
                                          checked={formData.course.includes(
                                            Number(course.course_id)
                                          )}
                                          onChange={() =>
                                            handleCourseChange(course.course_id)
                                          }
                                          className="w-4 h-4 rounded focus:ring-2"
                                          style={{ accentColor: subcategory.color }}
                                        />
                                        <span className="text-xs font-semibold">
                                          {course.course_item}
                                          {course.matchScore && course.matchScore < 100 && (
                                            <span className="ml-1 text-xs opacity-60">
                                              ({Math.round(course.matchScore)}%)
                                            </span>
                                          )}
                                          {formData.course.includes(
                                            Number(course.course_id)
                                          ) && (
                                            <svg
                                              className="inline ml-1 h-3 w-3"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="3"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M5 13l4 4L19 7"
                                              />
                                            </svg>
                                          )}
                                        </span>
                                      </label>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Show uncategorized courses if any */}
                  {categorizedData.uncategorized && categorizedData.uncategorized.length > 0 && (
                    <div className="mb-4 border-t pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">❓</span>
                        <div className="w-4 h-4 rounded-full bg-gray-400 border-2 border-white shadow-sm"></div>
                        <div className="font-semibold text-gray-600">
                          Uncategorized
                        </div>
                        <span className="text-xs text-gray-500 italic">
                          Courses that need manual tense map categorization
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {categorizedData.uncategorized.map((course) => (
                          <label
                            key={course.course_id}
                            className={`flex items-center gap-1 px-3 py-2 rounded-lg border-2 transition-all duration-150 cursor-pointer select-none
                              ${
                                formData.course.includes(Number(course.course_id))
                                  ? "bg-gray-500 border-gray-600 text-white shadow-md"
                                  : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                              }
                            `}
                          >
                            <input
                              type="checkbox"
                              value={course.course_id}
                              checked={formData.course.includes(
                                Number(course.course_id)
                              )}
                              onChange={() =>
                                handleCourseChange(course.course_id)
                              }
                              className="accent-gray-500 w-4 h-4 rounded focus:ring-2 focus:ring-gray-400"
                            />
                            <span className="text-xs font-semibold">
                              {course.course_item}
                              {formData.course.includes(
                                Number(course.course_id)
                              ) && (
                                <svg
                                  className="inline ml-1 h-3 w-3"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Original Navigation Paths View
                <>
                  {categorizedData.currentCategories?.map((cat) => (
                    <div key={cat.key} className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                          style={{ backgroundColor: cat.color }}
                        ></div>
                        <div className="font-semibold text-gray-800">
                          {cat.label}
                        </div>
                        {cat.navigationPath && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                            <span>{cat.navigationPath}</span>
                          </span>
                        )}
                        <span className="text-xs text-gray-500 italic">
                          {cat.description}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(categorizedData.categorizedCourses[cat.key] || []).length === 0 ? (
                          <span className="text-xs text-gray-400">
                            No courses found for this category.
                          </span>
                        ) : (
                          categorizedData.categorizedCourses[cat.key].map((course) => (
                            <label
                              key={course.course_id}
                              className={`flex items-center gap-1 px-3 py-2 rounded-lg border-2 transition-all duration-150 cursor-pointer select-none
                                ${
                                  formData.course.includes(Number(course.course_id))
                                    ? `text-white shadow-md border-gray-700`
                                    : `border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50`
                                }
                              `}
                              style={{
                                backgroundColor: formData.course.includes(
                                  Number(course.course_id)
                                )
                                  ? cat.color
                                  : "white",
                                borderColor: formData.course.includes(
                                  Number(course.course_id)
                                )
                                  ? cat.color
                                  : undefined,
                              }}
                            >
                              <input
                                type="checkbox"
                                value={course.course_id}
                                checked={formData.course.includes(
                                  Number(course.course_id)
                                )}
                                onChange={() =>
                                  handleCourseChange(course.course_id)
                                }
                                className="w-4 h-4 rounded focus:ring-2"
                                style={{ accentColor: cat.color }}
                              />
                              <span className="text-xs font-semibold">
                                {course.course_item}
                                {course.matchScore && course.matchScore < 100 && (
                                  <span className="ml-1 text-xs opacity-60">
                                    ({Math.round(course.matchScore)}%)
                                  </span>
                                )}
                                {formData.course.includes(
                                  Number(course.course_id)
                                ) && (
                                  <svg
                                    className="inline ml-1 h-3 w-3"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                )}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Question and Answers Section */}
        <div className="space-y-4">
          <div>
            <label
              htmlFor="question"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Question
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                type="text"
                name="question"
                id="question"
                value={formData.question}
                onChange={handleQuestionChange}
                className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-xl text-lg border-2 border-indigo-300 bg-white px-4 py-3 shadow-sm placeholder-gray-400"
                placeholder="It ____ a simple sentence"
                required
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Example: It ____ a simple sentence
            </p>
          </div>

          <div>
            <label
              htmlFor="answerA"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Correct Answer
            </label>
            <input
              type="text"
              name="answerA"
              id="answerA"
              value={formData.answers[0]}
              onChange={(e) => handleAnswerChange(0, e.target.value)}
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-xl text-lg border-2 border-green-300 bg-white px-4 py-3 shadow-sm placeholder-gray-400"
              placeholder="Correct answer"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label
                htmlFor="answerB"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Wrong Answer 1
              </label>
              <input
                type="text"
                name="answerB"
                id="answerB"
                value={formData.answers[1]}
                onChange={(e) => handleAnswerChange(1, e.target.value)}
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-xl text-lg border-2 border-red-200 bg-white px-4 py-3 shadow-sm placeholder-gray-400"
                placeholder="Wrong answer 1"
                required
              />
            </div>

            <div>
              <label
                htmlFor="answerC"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Wrong Answer 2
              </label>
              <input
                type="text"
                name="answerC"
                id="answerC"
                value={formData.answers[2]}
                onChange={(e) => handleAnswerChange(2, e.target.value)}
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-xl text-lg border-2 border-red-200 bg-white px-4 py-3 shadow-sm placeholder-gray-400"
                placeholder="Wrong answer 2"
                required
              />
            </div>

            <div>
              <label
                htmlFor="answerD"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Wrong Answer 3
              </label>
              <input
                type="text"
                name="answerD"
                id="answerD"
                value={formData.answers[3]}
                onChange={(e) => handleAnswerChange(3, e.target.value)}
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-xl text-lg border-2 border-red-200 bg-white px-4 py-3 shadow-sm placeholder-gray-400"
                placeholder="Wrong answer 3"
                required
              />
            </div>
          </div>

          <div className="pt-2">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Difficulty Level
            </label>
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <input
                  id="easy"
                  name="difficulty"
                  type="radio"
                  value="1"
                  checked={formData.difficulty === "1"}
                  onChange={handleDifficultyChange}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                />
                <label
                  htmlFor="easy"
                  className="ml-3 block text-sm font-medium text-gray-700"
                >
                  Easy
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="medium"
                  name="difficulty"
                  type="radio"
                  value="2"
                  checked={formData.difficulty === "2"}
                  onChange={handleDifficultyChange}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                />
                <label
                  htmlFor="medium"
                  className="ml-3 block text-sm font-medium text-gray-700"
                >
                  Medium
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="toeic"
                  name="difficulty"
                  type="radio"
                  value="3"
                  checked={formData.difficulty === "3"}
                  onChange={handleDifficultyChange}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                />
                <label
                  htmlFor="toeic"
                  className="ml-3 block text-sm font-medium text-gray-700"
                >
                  TOEIC
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-4">
          <motion.button
            type="submit"
            disabled={isSubmitting}
            className={`
              w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-md
              shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
              ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""}
            `}
            whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
            whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Adding Question...
              </>
            ) : (
              "Add Question"
            )}
          </motion.button>
        </div>
      </form>
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 rounded-md bg-green-50 border border-green-200"
        >
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                {successMessage}
              </p>
            </div>
          </div>
        </motion.div>
      )}
      {/* Questions List Section */}
      <div className="mt-10">
        {/* Search bar for filtering questions by keyword */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
          <div className="flex items-center gap-2 mb-2 md:mb-0">
            <label className="text-sm font-medium text-gray-700">Search questions:</label>
            <input
              type="text"
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Type a word or phrase..."
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
              style={{ minWidth: 200 }}
            />
          </div>
          <h2 className="text-xl font-bold text-gray-800">All Questions</h2>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              Filter by Course:
            </label>
            <select
              value={selectedCourseFilter}
              onChange={(e) => setSelectedCourseFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
            >
              <option value="">All Courses</option>
              {listCourse.map((course) => (
                <option key={course.course_id} value={course.course_id}>
                  {course.course_item}
                </option>
              ))}
            </select>
          </div>
        </div>
        {isLoadingQuestions ? (
          <div className="text-gray-500">Loading questions...</div>
        ) : questions.length === 0 ? (
          <div className="text-gray-500">{isAdmin ? "No questions found." : "You have not added any questions."}</div>
        ) : (
          (() => {
            // Apply admin/user filtering
            let filteredQuestions = questions;
            if (!isAdmin) {
              const userId = localStorage.getItem('user_id');
              filteredQuestions = filteredQuestions.filter(q => String(q.user_id) === String(userId));
            }
            // Apply course filter
            filteredQuestions = filteredQuestions.filter((q) => {
              if (!selectedCourseFilter) return true;
              // Handle comma-separated course_ids string
              if (typeof q.course_ids === 'string') {
                const courseIds = q.course_ids.split(',').map(id => id.trim());
                return courseIds.includes(String(selectedCourseFilter));
              }
              // Fallback for array format (backward compatibility)
              return (
                Array.isArray(q.course_ids) &&
                q.course_ids.includes(Number(selectedCourseFilter))
              );
            });
            // Apply search filter
            if (searchTerm.trim()) {
              const lower = searchTerm.trim().toLowerCase();
              filteredQuestions = filteredQuestions.filter(q => {
                // Search in question title
                const questionText = (q.question_title || q.title || '').toLowerCase();
                // Search in all answers
                const answers = Array.isArray(q.quiz_data)
                  ? q.quiz_data.join(' ').toLowerCase()
                  : [q.answer_title_a, q.answer_title_b, q.answer_title_c, q.answer_title_d].filter(Boolean).join(' ').toLowerCase();
                return questionText.includes(lower) || answers.includes(lower);
              });
            }

            if (selectedCourseFilter && filteredQuestions.length === 0) {
              const selectedCourse = listCourse.find(
                (course) =>
                  Number(course.course_id) === Number(selectedCourseFilter)
              );
              return (
                <div className="text-gray-500 text-center py-8">
                  <div className="text-lg font-medium">No questions found</div>
                  <div className="text-sm mt-1">
                    The course "
                    {selectedCourse?.course_item || "Selected course"}" has no
                    questions yet.
                  </div>
                </div>
              );
            }

            // Pagination calculations
            const totalQuestions = filteredQuestions.length;
            const totalPages = Math.ceil(totalQuestions / questionsPerPage);
            const startIndex = (currentPage - 1) * questionsPerPage;
            const endIndex = startIndex + questionsPerPage;
            const currentQuestions = filteredQuestions.slice(startIndex, endIndex);

            return (
              <div className="space-y-4">
                {/* Question count and pagination controls */}
                <div className="flex justify-between items-center mb-4">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1}-{Math.min(endIndex, totalQuestions)} of {totalQuestions} question
                    {totalQuestions !== 1 ? "s" : ""}
                    {selectedCourseFilter && (
                      <span>
                        {" "}
                        for "
                        {listCourse.find(
                          (c) =>
                            Number(c.course_id) === Number(selectedCourseFilter)
                        )?.course_item || "Selected course"}
                        "
                      </span>
                    )}
                  </div>
                  
                  {/* Items per page selector */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Show:</label>
                    <select
                      value={questionsPerPage}
                      onChange={(e) => handleQuestionsPerPageChange(Number(e.target.value))}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-600">per page</span>
                  </div>
                </div>

                {currentQuestions.map((q, i) => {
                  const questionId = q.question_id || q.id || i;
                  const isExpanded = expandedQuestions.has(questionId);

                  return (
                    <div
                      key={questionId}
                      className="bg-white rounded shadow p-4 border border-gray-100 relative"
                    >
                      {/* Compact view - Question title and courses */}
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                toggleQuestionExpansion(questionId)
                              }
                              className="text-indigo-600 hover:text-indigo-800 focus:outline-none"
                              title={
                                isExpanded
                                  ? "Collapse details"
                                  : "Expand details"
                              }
                            >
                              <svg
                                className={`h-4 w-4 transition-transform ${
                                  isExpanded ? "rotate-90" : ""
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </button>
                            <div className="font-semibold text-indigo-700">
                              {q.question_title || q.title}
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                q.verified
                                  ? "bg-green-100 text-green-800 border border-green-200"
                                  : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                              }`}
                            >
                              {q.verified ? "Verified" : "Pending"}
                            </span>
                          </div>

                          {/* Courses - always visible */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {(() => {
                              // Handle comma-separated course_ids string
                              let courseIds = [];
                              if (typeof q.course_ids === 'string' && q.course_ids.trim()) {
                                courseIds = q.course_ids.split(',').map(id => id.trim());
                              } else if (Array.isArray(q.course_ids)) {
                                courseIds = q.course_ids;
                              }
                              
                              return courseIds.length > 0 && listCourse.length > 0 ? (
                                courseIds.map((cid) => {
                                  const c = listCourse.find(
                                    (lc) => String(lc.course_id) === String(cid)
                                  );
                                  return (
                                    <span
                                      key={cid}
                                      className="inline-block px-2 py-1 bg-orange-100 text-orange-700 rounded-lg border border-orange-200 text-xs font-semibold shadow-sm"
                                    >
                                      {c ? c.course_item : `Course ${cid}`}
                                    </span>
                                  );
                                })
                              ) : (
                                <span className="text-xs text-gray-400">
                                  No courses
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Action buttons - always visible */}
                        <div className="flex gap-2 ml-4">
                          {!q.verified && isAdmin && (
                            <button
                              onClick={() => handleApproveQuestion(q)}
                              className="text-xs px-2 py-1 bg-green-100 hover:bg-green-200 text-green-800 rounded border border-green-300"
                              title="Approve this question"
                            >
                              Approve
                            </button>
                          )}
                          <button
                            onClick={() => handleEditQuestion(q)}
                            className="text-xs px-2 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded border border-yellow-300"
                            title="Edit this question"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(q.question_id)}
                            className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded border border-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Expanded view - Answers and difficulty */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="text-sm">
                            <div className="font-semibold mb-2">Answers:</div>
                            <ul className="space-y-1 list-inside">
                              <li className="flex items-center text-green-700 bg-green-50 px-2 py-1 rounded">
                                <svg
                                  className="h-4 w-4 mr-2 flex-shrink-0"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>
                                  {Array.isArray(q.quiz_data)
                                    ? q.quiz_data[0]
                                    : q.answer_title_a}
                                </span>
                              </li>
                              {(Array.isArray(q.quiz_data)
                                ? q.quiz_data.slice(1)
                                : [
                                    q.answer_title_b,
                                    q.answer_title_c,
                                    q.answer_title_d,
                                  ]
                              )
                                .filter(Boolean)
                                .map((ans, idx) => (
                                  <li
                                    key={idx}
                                    className="flex items-center text-red-700 bg-red-50 px-2 py-1 rounded"
                                  >
                                    <svg
                                      className="h-4 w-4 mr-2 flex-shrink-0"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <span>{ans}</span>
                                  </li>
                                ))}
                            </ul>
                          </div>
                          <div className="text-sm text-gray-600 mt-2">
                            <strong>Difficulty:</strong> {q.difficulty || "-"}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2 mt-6 pt-4 border-t border-gray-200">
                    {/* Previous Button */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        currentPage === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    {(() => {
                      const pages = [];
                      const showPages = 5; // Show 5 page numbers at most
                      let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                      let endPage = Math.min(totalPages, startPage + showPages - 1);
                      
                      // Adjust start if we're near the end
                      if (endPage - startPage < showPages - 1) {
                        startPage = Math.max(1, endPage - showPages + 1);
                      }

                      // First page + ellipsis
                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => handlePageChange(1)}
                            className="px-3 py-2 rounded-md text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span key="start-ellipsis" className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                      }

                      // Page numbers
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => handlePageChange(i)}
                            className={`px-3 py-2 rounded-md text-sm font-medium ${
                              i === currentPage
                                ? "bg-blue-600 text-white"
                                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }

                      // Last page + ellipsis
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span key="end-ellipsis" className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => handlePageChange(totalPages)}
                            className="px-3 py-2 rounded-md text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                          >
                            {totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}

                    {/* Next Button */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        currentPage === totalPages
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            );
          })()
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingQuestion && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit Question
              </h3>
              <EditQuestionForm
                question={editingQuestion}
                courses={listCourse}
                onSave={handleUpdateQuestion}
                onCancel={() => {
                  setShowEditModal(false);
                  setEditingQuestion(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );

  // Only wrap in AdminLayout if used as standalone component
  return standalone ? (
    <AdminLayout title="Quiz Builder">{content}</AdminLayout>
  ) : (
    content
  );
};

// --- CRUD Handlers are now inside the FormAddQuestion component, below useState hooks ---
// (No hooks at top level)

export default FormAddQuestion;
